import { fetchNRELWeatherData, type NRELWeatherData } from './nrelWeatherService';

export interface WeatherData {
  latitude: number;
  longitude: number;
  monthlyGHI: number[]; // kWh/m2/day
  monthlyDNI: number[]; // kWh/m2/day
  monthlyDHI: number[]; // kWh/m2/day
  monthlyTemp: number[]; // C
  monthlyWindSpeed: number[]; // m/s
  annualGHI: number; // kWh/m2/year
  source: 'nrel';
}

export interface WeatherDataSource {
  source: 'nrel';
  accuracy: 'high';
  location: string;
  message: string;
}

export interface LocationInput {
  latitude: number;
  longitude: number;
}

function normalizeNRELFailure(error: unknown, coordName: string): Error {
  const message = error instanceof Error ? error.message : String(error);
  const lower = message.toLowerCase();

  if (lower.includes('vite_nrel_api_key') || lower.includes('vite_nrel_email')) {
    return new Error(
      `Missing NREL configuration for ${coordName}. Set VITE_NREL_API_KEY and VITE_NREL_EMAIL.`
    );
  }

  if (lower.includes('429') || lower.includes('rate limit')) {
    return new Error(`NREL rate limit reached for ${coordName}. Retry after a short delay.`);
  }

  if (lower.includes('no data') || lower.includes('returned errors for this location')) {
    return new Error(`NREL has no weather data for ${coordName}. Try nearby coordinates.`);
  }

  if (
    lower.includes('timed out') ||
    lower.includes('network') ||
    lower.includes('failed to fetch') ||
    lower.includes('abort')
  ) {
    return new Error(`NREL network error for ${coordName}. Check internet connectivity and try again.`);
  }

  return new Error(`Failed to fetch NREL weather data for ${coordName}: ${message}`);
}

export async function getWeatherData(location: LocationInput): Promise<{ data: WeatherData; source: WeatherDataSource }> {
  const latitude = location.latitude;
  const longitude = location.longitude;

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    throw new Error('Invalid location input: latitude and longitude are required.');
  }

  const coordName = `${latitude.toFixed(4)} deg, ${longitude.toFixed(4)} deg`;

  try {
    const nrel: NRELWeatherData = await fetchNRELWeatherData(latitude, longitude);
    return {
      data: { ...nrel, source: 'nrel' },
      source: {
        source: 'nrel',
        accuracy: 'high',
        location: coordName,
        message: `Using NREL NSRDB MSG weather data for ${coordName}.`,
      },
    };
  } catch (error) {
    throw normalizeNRELFailure(error, coordName);
  }
}
